import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import './SupervisorSparesMonitor.css';

const CATEGORIES = [
  { value: 'all',         label: 'All Categories'    },
  { value: 'seals',       label: '🔵 Seals & O-Rings' },
  { value: 'valves',      label: '🔴 Valves'          },
  { value: 'pumps',       label: '⚙️ Pump Parts'      },
  { value: 'taps',        label: '🚰 Taps & Fittings' },
  { value: 'hoses',       label: '🟡 Hoses & Tubing'  },
  { value: 'connectors',  label: '🔗 Connectors'      },
  { value: 'electrical',  label: '⚡ Electrical'       },
  { value: 'cleaning',    label: '🧹 Cleaning'        },
  { value: 'other',       label: '📦 Other'           },
];

function SupervisorSparesMonitor({ user, onBack }) {
  const [spares, setSpares]           = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterDays, setFilterDays]   = useState(30);
  const [filterTech, setFilterTech]   = useState('all');
  const [filterCat, setFilterCat]     = useState('all');
  const [activeTab, setActiveTab]     = useState('overview');  // overview | by-tech | by-outlet | list

  useEffect(() => { loadAll(); }, [filterDays]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const since = new Date();
      since.setDate(since.getDate() - filterDays);
      since.setHours(0, 0, 0, 0);

      const [sparesSnap, techSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'sparesUsed'),
          where('timestamp', '>=', since.getTime()),
          orderBy('timestamp', 'desc')
        )),
        getDocs(query(
          collection(db, 'users'),
          where('role', '==', 'technician'),
          where('active', '==', true)
        ))
      ]);

      setSpares(sparesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTechnicians(techSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('❌ Load spares error:', e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = spares.filter(s => {
    if (filterTech !== 'all' && s.userId !== filterTech) return false;
    if (filterCat  !== 'all' && s.category !== filterCat)  return false;
    return true;
  });

  // Overall stats
  const getStats = () => ({
    totalEntries: filtered.length,
    totalParts:   filtered.reduce((a, s) => a + s.quantity, 0),
    totalCost:    filtered.reduce((a, s) => a + (s.totalCost || 0), 0),
    activeTechs:  new Set(filtered.map(s => s.userId)).size,
  });

  // By technician
  const getByTech = () => {
    const map = {};
    filtered.forEach(s => {
      if (!map[s.userId]) map[s.userId] = { name: s.userName, parts: 0, cost: 0, entries: 0 };
      map[s.userId].parts   += s.quantity;
      map[s.userId].cost    += s.totalCost || 0;
      map[s.userId].entries += 1;
    });
    return Object.entries(map)
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.cost - a.cost);
  };

  // By outlet
  const getByOutlet = () => {
    const map = {};
    filtered.filter(s => s.outletName).forEach(s => {
      if (!map[s.outletName]) map[s.outletName] = { name: s.outletName, parts: 0, cost: 0, entries: 0 };
      map[s.outletName].parts   += s.quantity;
      map[s.outletName].cost    += s.totalCost || 0;
      map[s.outletName].entries += 1;
    });
    return Object.values(map).sort((a, b) => b.cost - a.cost);
  };

  // By category
  const getByCategory = () => {
    const map = {};
    filtered.forEach(s => {
      if (!map[s.category]) map[s.category] = { category: s.category, parts: 0, cost: 0 };
      map[s.category].parts += s.quantity;
      map[s.category].cost  += s.totalCost || 0;
    });
    return Object.values(map).sort((a, b) => b.cost - a.cost);
  };

  const getCatLabel = (val) => CATEGORIES.find(c => c.value === val)?.label || val;

  const formatDate = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleDateString('en-KE', { day: 'numeric', month: 'short' });
  };

  const exportCSV = () => {
    let csv = `Spares Usage Report - Last ${filterDays} days\nGenerated: ${new Date().toLocaleString('en-KE')}\n\n`;
    csv += `Technician,Part Name,Category,Quantity,Unit Cost (KSh),Total Cost (KSh),Outlet,Date\n`;
    filtered.forEach(s => {
      csv += `"${s.userName}","${s.partName}","${getCatLabel(s.category)}","${s.quantity}","${s.unitCost||0}","${s.totalCost||0}","${s.outletName||''}","${formatDate(s.timestamp)}"\n`;
    });

    // Summary by tech
    csv += `\nSummary by Technician\nName,Total Parts,Total Cost (KSh),Entries\n`;
    getByTech().forEach(t => {
      csv += `"${t.name}","${t.parts}","${t.cost}","${t.entries}"\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `spares_report_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = getStats();
  const byTech = getByTech();
  const byOutlet = getByOutlet();
  const byCategory = getByCategory();
  const maxCost = Math.max(...byTech.map(t => t.cost), 1);

  return (
    <div className="ssm-page">
      {/* Header */}
      <div className="ssm-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <div className="ssm-header-text">
          <h1>🔩 Spares Monitor</h1>
          <p>Last {filterDays} days</p>
        </div>
        <button className="ssm-export-btn" onClick={exportCSV} disabled={filtered.length === 0}>
          ⬇️ Export
        </button>
      </div>

      {/* Filters */}
      <div className="ssm-filters">
        <div className="day-filter-pills">
          {[7, 14, 30, 90].map(d => (
            <button key={d} className={`day-pill ${filterDays === d ? 'active' : ''}`}
              onClick={() => setFilterDays(d)}>
              {d}d
            </button>
          ))}
        </div>
        <select className="ssm-select" value={filterTech} onChange={e => setFilterTech(e.target.value)}>
          <option value="all">All Technicians</option>
          {technicians.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className="ssm-select" value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>

      {/* Stat Cards */}
      <div className="ssm-stats">
        {[
          { label: 'Total Entries',  val: stats.totalEntries,                          color: 'var(--sv-ink)'    },
          { label: 'Parts Used',     val: stats.totalParts,                            color: 'var(--sv-maroon)' },
          { label: 'Active Techs',   val: stats.activeTechs,                           color: 'var(--sv-success)' },
          { label: 'Total Cost',     val: `KSh ${stats.totalCost.toLocaleString()}`,   color: '#92722E'          },
        ].map(s => (
          <div className="ssm-stat" key={s.label}>
            <span className="ssm-stat-val" style={{ color: s.color }}>{s.val}</span>
            <span className="ssm-stat-lbl">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="ssm-tabs">
        {['overview','by-tech','by-outlet','list'].map(tab => (
          <button key={tab}
            className={`ssm-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}>
            {tab === 'overview' ? '📊 Overview'
            : tab === 'by-tech' ? '👤 By Tech'
            : tab === 'by-outlet' ? '🏪 By Outlet'
            : '📋 All Entries'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-state"><div className="spinner"></div><p>Loading data...</p></div>
      ) : filtered.length === 0 ? (
        <div className="ssm-empty">
          <div className="empty-icon">📭</div>
          <p>No spares data found for selected filters</p>
        </div>
      ) : (
        <>
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="ssm-content">
              <div className="ssm-section-card">
                <h3>Cost by Technician</h3>
                {byTech.map(t => (
                  <div key={t.id} className="ssm-bar-row">
                    <div className="ssm-bar-label">
                      <span className="ssm-bar-name">{t.name}</span>
                      <span className="ssm-bar-val">KSh {t.cost.toLocaleString()}</span>
                    </div>
                    <div className="ssm-bar-track">
                      <div className="ssm-bar-fill" style={{ width: `${(t.cost / maxCost) * 100}%` }}></div>
                    </div>
                    <div className="ssm-bar-sub">{t.parts} parts · {t.entries} entries</div>
                  </div>
                ))}
              </div>

              <div className="ssm-section-card">
                <h3>By Category</h3>
                <div className="ssm-cat-grid">
                  {byCategory.map(c => (
                    <div key={c.category} className="ssm-cat-card">
                      <div className="ssm-cat-label">{getCatLabel(c.category)}</div>
                      <div className="ssm-cat-parts">{c.parts} parts</div>
                      {c.cost > 0 && <div className="ssm-cat-cost">KSh {c.cost.toLocaleString()}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* BY TECH TAB */}
          {activeTab === 'by-tech' && (
            <div className="ssm-content">
              {byTech.map((t, i) => (
                <div key={t.id} className="ssm-tech-card">
                  <div className="ssm-tech-header">
                    <div className="ssm-tech-rank">
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i+1}`}
                    </div>
                    <div className="ssm-tech-avatar">{t.name?.charAt(0).toUpperCase()}</div>
                    <div className="ssm-tech-info">
                      <div className="ssm-tech-name">{t.name}</div>
                      <div className="ssm-tech-sub">{t.entries} entries · {t.parts} parts</div>
                    </div>
                    <div className="ssm-tech-cost">KSh {t.cost.toLocaleString()}</div>
                  </div>
                  <div className="ssm-tech-entries">
                    {filtered.filter(s => s.userId === t.id).slice(0,5).map(s => (
                      <div key={s.id} className="ssm-mini-entry">
                        <span className="ssm-mini-part">{s.partName}</span>
                        <span className="ssm-mini-qty">×{s.quantity}</span>
                        {s.totalCost > 0 && <span className="ssm-mini-cost">KSh {s.totalCost.toLocaleString()}</span>}
                        <span className="ssm-mini-date">{formatDate(s.timestamp)}</span>
                      </div>
                    ))}
                    {filtered.filter(s => s.userId === t.id).length > 5 && (
                      <div className="ssm-more">+ {filtered.filter(s => s.userId === t.id).length - 5} more entries</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* BY OUTLET TAB */}
          {activeTab === 'by-outlet' && (
            <div className="ssm-content">
              {byOutlet.length === 0 ? (
                <div className="ssm-empty"><p>No outlet-linked spares in this period</p></div>
              ) : byOutlet.map(o => (
                <div key={o.name} className="ssm-outlet-card">
                  <div className="ssm-outlet-header">
                    <div className="ssm-outlet-icon">🏪</div>
                    <div className="ssm-outlet-info">
                      <div className="ssm-outlet-name">{o.name}</div>
                      <div className="ssm-outlet-sub">{o.entries} entries · {o.parts} parts</div>
                    </div>
                    <div className="ssm-outlet-cost">KSh {o.cost.toLocaleString()}</div>
                  </div>
                  <div className="ssm-outlet-parts">
                    {filtered.filter(s => s.outletName === o.name).slice(0,4).map(s => (
                      <div key={s.id} className="ssm-mini-entry">
                        <span className="ssm-mini-part">{s.partName}</span>
                        <span className="ssm-mini-by">by {s.userName}</span>
                        <span className="ssm-mini-qty">×{s.quantity}</span>
                        {s.totalCost > 0 && <span className="ssm-mini-cost">KSh {s.totalCost.toLocaleString()}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ALL ENTRIES TAB */}
          {activeTab === 'list' && (
            <div className="ssm-content">
              <div className="ssm-entries-list">
                {filtered.map(s => (
                  <div key={s.id} className="ssm-entry-row">
                    <div className="ssm-entry-body">
                      <div className="ssm-entry-top">
                        <span className="ssm-entry-name">{s.partName}</span>
                        <span className="ssm-entry-qty">×{s.quantity}</span>
                      </div>
                      <div className="ssm-entry-meta">
                        👤 {s.userName} · {getCatLabel(s.category)}
                        {s.outletName ? ` · 🏪 ${s.outletName}` : ''}
                        · {formatDate(s.timestamp)}
                      </div>
                    </div>
                    {s.totalCost > 0 && (
                      <div className="ssm-entry-cost">KSh {s.totalCost.toLocaleString()}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SupervisorSparesMonitor;
